import { com } from '@eidu/web'
import React, { ReactNode, useMemo, useState } from 'react'
import { Select } from '@common/ui'
import { useCreateSemanticLink } from '../../io/useSemanticLinks'
import CustomSemanticLinkKeyFields from './CustomSemanticLinkKeyFields'
import SemanticLinkFormShell from './SemanticLinkFormShell'
import SemanticLinkTargetFields from './SemanticLinkTargetFields'
import useCustomSemanticLinkKey, { predefinedConfigsForKind } from './useCustomSemanticLinkKey'
import EntityType = com.eidu.entity.type.EntityType
import EntityPath = com.eidu.entity.path.EntityPath
import FieldType = com.eidu.entity.field.FieldType

const CUSTOM_KEY_SENTINEL = '__custom__'

type AddSemanticLinkFormProps = {
  entityType: EntityType
  entityTypes: readonly EntityType[]
  onSave: () => void
  onCancel: () => void
  children?: ReactNode
}

const AddSemanticLinkForm = ({ entityType, entityTypes, onSave, onCancel, children }: AddSemanticLinkFormProps) => {
  const predefinedConfigs = useMemo(() => predefinedConfigsForKind(entityType.kind), [entityType.kind])
  const customKey = useCustomSemanticLinkKey(predefinedConfigs)

  const [selectedKeyValue, setSelectedKeyValue] = useState('')
  const [fieldType, setFieldType] = useState<FieldType>(FieldType.Reference)
  const [selectedPath, setSelectedPath] = useState<EntityPath>()
  const { save, isSaving } = useCreateSemanticLink(onSave)

  const isCustom = selectedKeyValue === CUSTOM_KEY_SENTINEL
  const selectedPredefined = isCustom ? undefined : predefinedConfigs.find((c) => c.key.toString() === selectedKeyValue)
  const resolvedKey = selectedPredefined?.key ?? (isCustom ? customKey.resolvedKey : null)

  const toTypeKind = selectedPredefined?.toTypeKind ?? null
  const targetTypes = entityTypes.filter(({ kind }) => (toTypeKind ? kind.equals(toTypeKind) : true))

  return (
    <SemanticLinkFormShell
      isSaving={isSaving}
      canSave={!!selectedPath && !!resolvedKey}
      onSave={() => resolvedKey && selectedPath && save(resolvedKey, selectedPath)}
      onCancel={onCancel}
    >
      {children}
      <Select
        label="Semantic link key"
        value={selectedKeyValue || null}
        onChange={(value) => {
          if (value === null) return
          setSelectedKeyValue(value)
          customKey.reset()
          setSelectedPath(undefined)
        }}
        data={[
          ...predefinedConfigs.map((config) => ({
            value: config.key.toString(),
            label: `${config.title} (${config.key.toString()})`,
          })),
          { value: CUSTOM_KEY_SENTINEL, label: 'Custom key...' },
        ]}
      />
      {isCustom && (
        <CustomSemanticLinkKeyFields
          customKey={customKey}
          fieldType={fieldType}
          onFieldTypeChange={(type) => {
            setFieldType(type)
            setSelectedPath(undefined)
          }}
        />
      )}
      {selectedPredefined && (
        <Select
          label="Field type"
          value={selectedPredefined.fieldType.toString()}
          data={[selectedPredefined.fieldType.toString()]}
          disabled
        />
      )}
      {(selectedPredefined || isCustom) && (
        <SemanticLinkTargetFields
          sourceTypeId={entityType.id}
          entityTypes={entityTypes}
          targetTypes={targetTypes}
          fieldType={selectedPredefined ? selectedPredefined.fieldType : fieldType}
          selectedPath={selectedPath}
          setSelectedPath={setSelectedPath}
        />
      )}
    </SemanticLinkFormShell>
  )
}

export default AddSemanticLinkForm
